/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { GmcError, GmcNotificationAction } from '@app/core/types';
import { createAction, props } from '@ngrx/store';
import { ClusterId } from '@shared/types/cluster';
import {
  Cluster3xConfiguration,
  Cluster3xMonitoringConfigurationRequestBody,
  Cluster3xTopology,
  ClusterConnectors,
  InitializeCluster3xRequestBody,
} from '@shared/types/cluster3x';
import { Cluster3xDistributionZone } from '@shared/types/distribution-zone';
import { Cluster3xLocalPartitionStates } from '@shared/types/partition-states';
import { Cluster3xTable } from '@shared/types/tables';

export const initializeCluster = createAction('[Cluster 3x] Initialize', props<InitializeCluster3xRequestBody>());
export const initializeClusterOK = createAction('[Cluster 3x] Initialize OK', props<{ notification?: string }>());
export const initializeClusterErr = createAction('[Cluster 3x] Initialize Err', props<GmcError>());

export const loadIgnite3PhysicalTopologyErr = createAction(
  '[Cluster 3x] Load physical topology err',
  props<GmcError>(),
);

export const cluster3xTopology = createAction(
  '[Cluster] Cluster 3x topology',
  props<{ clusterId: ClusterId; data: Cluster3xTopology }>(),
);

// Configure connection

export const configureConnectionOk = createAction(
  '[Cluster 3x] Configure connection ok',
  props<GmcNotificationAction>(),
);

export const configureConnectionErr = createAction(
  '[Cluster 3x] Configure connection err',
  props<GmcError & { clusterId: ClusterId }>(),
);

// Tables and Distribution zones

export const loadCluster3xTables = createAction(
  '[Cluster 3x] Load Cluster 3x tables',
  props<{ clusterId: ClusterId }>(),
);

export const cluster3xTables = createAction(
  '[Cluster 3x] Cluster 3x tables',
  props<{ clusterId: ClusterId; data: Cluster3xTable[] }>(),
);

export const cluster3xTablesErr = createAction(
  '[Cluster 3x] Cluster 3x tables err',
  props<GmcError & { clusterId: ClusterId }>(),
);

export const loadCluster3xDistributionZones = createAction(
  '[Cluster 3x] Load Cluster 3x distribution zones',
  props<{ clusterId: ClusterId }>(),
);

export const cluster3xDistributionZones = createAction(
  '[Cluster 3x] Cluster 3x distribution zones',
  props<{ clusterId: ClusterId; data: Cluster3xDistributionZone[] }>(),
);

export const cluster3xDistributionZonesErr = createAction(
  '[Cluster 3x] Cluster 3x distribution zones err',
  props<GmcError & { clusterId: ClusterId }>(),
);

// Local and Global partitions

export const loadCluster3xLocalPartitionState = createAction(
  '[Cluster 3x] Load Cluster 3x local partition state',
  props<{ clusterId: ClusterId }>(),
);

export const cluster3xLocalPartitionStates = createAction(
  '[Cluster 3x] Cluster 3x local partition state',
  props<{ clusterId: ClusterId; data: Cluster3xLocalPartitionStates }>(),
);

export const cluster3xLocalPartitionStateErr = createAction(
  '[Cluster 3x] Cluster 3x local partition state err',
  props<GmcError & { clusterId: ClusterId }>(),
);

// Cluster 3x Configuration

export const getCluster3xConfiguration = createAction(
  '[Cluster 3x] Get Cluster 3x configuration',
  props<{ clusterId: ClusterId }>(),
);

export const cluster3xConfiguration = createAction(
  '[Cluster 3x] Cluster 3x configuration',
  props<{ clusterId: ClusterId; configuration: Cluster3xConfiguration }>(),
);

export const cluster3xConfigurationErr = createAction(
  '[Cluster 3x] Cluster 3x configuration err',
  props<GmcError & { clusterId: ClusterId }>(),
);

export const cluster3xUpdateConfiguration = createAction(
  '[Cluster 3x] Update cluster configuration',
  props<{ clusterId: ClusterId; configuration: Cluster3xConfiguration }>(),
);

export const cluster3xUpdateConfigurationOk = createAction(
  '[Cluster 3x] Update cluster configuration OK',
  props<GmcNotificationAction & { clusterId: ClusterId }>(),
);

export const cluster3xUpdateConfigurationErr = createAction(
  '[Cluster 3x] Update cluster configuration err',
  props<GmcError & { clusterId: ClusterId }>(),
);

export const cluster3xUpdateClusterMonitoringConfiguration = createAction(
  '[Cluster 3x] Update cluster monitoring configuration',
  props<{ clusterId: ClusterId; configuration: Cluster3xMonitoringConfigurationRequestBody }>(),
);
export const cluster3xUpdateClusterMonitoringConfigurationOk = createAction(
  '[Cluster 3x] Update cluster monitoring configuration OK',
  props<GmcNotificationAction & { clusterId: ClusterId }>(),
);

export const cluster3xUpdateClusterMonitoringConfigurationErr = createAction(
  '[Cluster 3x] Update cluster monitoring configuration err',
  props<GmcError & { clusterId: ClusterId }>(),
);

// Connectors

export const clusterConnectorsAction = createAction('[Cluster 3x] Connectors', props<{ data: ClusterConnectors }>());
